"use client";

import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import SudokuBoard from "@/components/tools/games-organizers/sudoku-board";
import SudokuSheet from "@/components/tools/games-organizers/sudoku-sheet";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  DIFFICULTIES,
  type Difficulty,
  difficultyOfTier,
  type GeneratedPuzzle,
  generatePuzzle,
  toPuzzleString,
} from "@/lib/tool-kit/games-organizers/sudoku";
import {
  formatSeed,
  newSeed,
  parseSeed,
} from "@/lib/tool-kit/worksheets/random";

/** English strings. A translated content file overrides these through `ui`. */
export const SUDOKU_GENERATOR_STRINGS = {
  difficultyLabel: "Difficulty",
  difficulties: {
    easy: "Easy",
    medium: "Medium",
    hard: "Hard",
    expert: "Expert",
  },
  difficultyHints: {
    easy: "Solved with naked and hidden singles alone.",
    medium: "Needs locked candidates (pointing and claiming) as well.",
    hard: "Needs naked or hidden pairs and triples as well.",
    expert: "Needs an X-Wing, or something harder.",
  },
  techniques: {
    "naked-single": "naked singles",
    "hidden-single": "hidden singles",
    "locked-candidates": "locked candidates",
    "naked-subset": "naked pairs and triples",
    "hidden-subset": "hidden pairs and triples",
    "x-wing": "X-Wing",
  },
  symmetric: "Symmetric givens (180° rotation)",
  seedLabel: "Puzzle number",
  seedHint: "Type a number from 1 to 999999 to get the same puzzle again.",
  seedInvalid: "Use a whole number from 1 to 999999.",
  newPuzzle: "New puzzle",
  fromSeed: "Make puzzle from this number",
  preparing: "Preparing a puzzle…",
  heading: "Your puzzle",
  title: "Sudoku — {difficulty}",
  puzzleNumber: "Puzzle {seed}",
  givens: "one: {count} given|other: {count} givens",
  ratedLine:
    "Rated {difficulty} — the hardest technique needed was {technique}.",
  ratedBeyond:
    "Rated beyond Expert — it cannot be finished with the techniques this page knows (up to X-Wing), so it may need chains or trial and error.",
  offTarget:
    "The generator could not land exactly on {requested}, so this puzzle is rated {actual}. Press New puzzle to try again.",
  uniqueNote: "Checked: exactly one solution.",
  printPuzzle: "Print puzzle",
  printKey: "Print puzzle and answers",
  answerKeyTitle: "Answer key",
  copyString: "Copy as 81 characters",
  copied: "Copied!",
  printGridLabel: "Sudoku puzzle",
  keyGridLabel: "Sudoku answers",
  boardLabel: "Sudoku board",
  cellLabel: "Row {row}, column {col}",
  cellEmpty: "empty",
  cellGiven: "{digit}, given",
  cellEntered: "{digit}",
  cellNotes: "notes {digits}",
  cellWrong: "incorrect",
  enterDigit: "Enter {digit}",
  erase: "Erase",
  notesOn: "Notes: on",
  notesOff: "Notes: off",
  undo: "Undo",
  hint: "Hint",
  clearEntries: "Clear my entries",
  showMistakes: "Highlight mistakes",
  keyboardHint:
    "Select a square, then type 1–9. Backspace clears, arrow keys move, N switches notes on and off.",
  solved: "Solved — every square is correct.",
} as const;

type Strings = typeof SUDOKU_GENERATOR_STRINGS;
type PrintTarget = "puzzle" | "key";

const SHEET_H2 = "font-semibold text-xl";

function nameOf(strings: Strings, difficulty: Difficulty | null): string {
  return difficulty ? strings.difficulties[difficulty] : "";
}

function ratingText(strings: Strings, result: GeneratedPuzzle): string {
  const { rating, requested } = result;
  const actual = difficultyOfTier(rating.tier);
  if (!(rating.solved && rating.hardest)) {
    return strings.ratedBeyond;
  }
  const line = t(strings.ratedLine, {
    difficulty: nameOf(strings, actual),
    technique: strings.techniques[rating.hardest],
  });
  if (result.onTarget) {
    return line;
  }
  return `${line} ${t(strings.offTarget, {
    requested: nameOf(strings, requested),
    actual: nameOf(strings, actual),
  })}`;
}

export default function SudokuGeneratorWidget() {
  const strings = useToolUi(SUDOKU_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const [difficulty, setDifficulty] = useState<Difficulty>("medium");
  const [symmetric, setSymmetric] = useState(true);
  const [seedText, setSeedText] = useState("");
  const [seedError, setSeedError] = useState(false);
  const [result, setResult] = useState<GeneratedPuzzle | null>(null);
  const [printTarget, setPrintTarget] = useState<PrintTarget>("puzzle");

  function make(seed: number) {
    setResult(generatePuzzle(seed, difficulty, symmetric));
    setSeedText(String(seed));
    setSeedError(false);
  }

  // The first puzzle is made after mount: the page is static HTML first, and a random
  // puzzle in the initial render would differ between server and browser.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs once, on mount
  useEffect(() => {
    make(newSeed());
  }, []);

  function fromSeed() {
    const seed = parseSeed(seedText);
    if (seed === null) {
      setSeedError(true);
      return;
    }
    make(seed);
  }

  function print(target: PrintTarget) {
    flushSync(() => setPrintTarget(target));
    window.print();
  }

  const title = t(strings.title, {
    difficulty: nameOf(
      strings,
      result
        ? (difficultyOfTier(result.rating.tier) ?? result.requested)
        : difficulty
    ),
  });
  const sheet = "space-y-4 text-foreground print:text-black";

  return (
    <div className="space-y-6">
      <div className="space-y-4 print:hidden">
        <PillGroup
          label={strings.difficultyLabel}
          name="sudoku-difficulty"
          onChange={setDifficulty}
          options={DIFFICULTIES.map((value) => ({
            value,
            label: strings.difficulties[value],
          }))}
          value={difficulty}
        />
        <p className={HINT}>{strings.difficultyHints[difficulty]}</p>
        <Checkbox
          checked={symmetric}
          label={strings.symmetric}
          onChange={setSymmetric}
        />
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_PRIMARY}
            onClick={() => make(newSeed())}
            type="button"
          >
            {strings.newPuzzle}
          </button>
        </div>
        <div className="grid items-end gap-3 sm:grid-cols-[1fr_auto]">
          <TextField
            hint={strings.seedHint}
            id="sudoku-seed"
            label={strings.seedLabel}
            mono
            numeric
            onChange={setSeedText}
            value={seedText}
          />
          <button className={BUTTON_SECONDARY} onClick={fromSeed} type="button">
            {strings.fromSeed}
          </button>
        </div>
        {seedError ? <ErrorNotice>{strings.seedInvalid}</ErrorNotice> : null}
      </div>

      {result ? (
        <>
          <div aria-live="polite" className="space-y-1 print:hidden">
            <h2 className={WIDGET_H2}>{strings.heading}</h2>
            <p className={HINT}>
              {t(strings.puzzleNumber, { seed: formatSeed(result.seed) })} ·{" "}
              {tPlural(strings.givens, locale, result.givens)}
            </p>
            <p className="text-foreground text-sm">
              {ratingText(strings, result)}
            </p>
            <p className={HINT}>{strings.uniqueNote}</p>
          </div>
          <SudokuBoard
            key={`${result.seed}-${result.requested}-${result.symmetric}`}
            puzzle={result.puzzle}
            solution={result.solution}
            strings={strings}
          />
          <div className="flex flex-wrap justify-center gap-3 print:hidden">
            <button
              className={BUTTON_SECONDARY}
              onClick={() => print("puzzle")}
              type="button"
            >
              {strings.printPuzzle}
            </button>
            <button
              className={BUTTON_SECONDARY}
              onClick={() => print("key")}
              type="button"
            >
              {strings.printKey}
            </button>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyString}
              text={toPuzzleString(result.puzzle)}
            />
          </div>
          <section className={`hidden print:block ${sheet}`}>
            <h2 className={SHEET_H2}>{title}</h2>
            <p className="text-sm">
              {t(strings.puzzleNumber, { seed: formatSeed(result.seed) })}
            </p>
            <SudokuSheet
              label={strings.printGridLabel}
              puzzle={result.puzzle}
            />
          </section>
          {printTarget === "key" ? (
            <section
              className={`hidden break-before-page print:block ${sheet}`}
            >
              <h2 className={SHEET_H2}>
                {title} — {strings.answerKeyTitle}
              </h2>
              <SudokuSheet
                label={strings.keyGridLabel}
                puzzle={result.puzzle}
                solution={result.solution}
              />
            </section>
          ) : null}
        </>
      ) : (
        <Notice>{strings.preparing}</Notice>
      )}
    </div>
  );
}
